/**
 * Unified file preview dialog.
 *
 * One component, every caller. Renders modal chrome (header + actions +
 * footer) around a renderer dispatched from the registry. Sources for
 * different content backends (URL, artifact, inline) plug in via the
 * factories in `../sources`.
 *
 * Replaces — directly or via thin wrappers — the four legacy preview
 * dialogs that each duplicated header / footer / renderer-dispatch
 * logic: `attachment-preview-dialog`, tool-call `file-preview-dialog`,
 * `library-preview-dialog`, and artifact `document-preview`.
 */
import { FileText, Maximize2, Minimize2, X } from "lucide-react";
import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { DownloadAction } from "../actions/download-action";
import { OpenExternalAction } from "../actions/open-external-action";
import type { FilePreviewDialogProps } from "../types";
import { formatBytes } from "../utils/format-bytes";

import { FilePreviewBody } from "./file-preview-body";

/**
 * Renderer kinds whose content sits inside a fixed max-width frame and
 * is centered horizontally when the dialog is maximized. Includes:
 *   - text-like (markdown, code, text) — keeps prose readable instead
 *     of stretching across a 2000px viewport
 *   - pdf — documents are page-shaped; full-bleed wastes screen
 *   - image — centered with its checker backdrop, capped so giant
 *     uploads don't blow out the layout
 *
 * Edge-to-edge kinds (intentionally NOT in this set):
 *   - csv / spreadsheet — tabular, benefits from the extra width
 *   - html — iframes may contain full-bleed designs; the sandbox
 *     controls its own width
 *   - presentation — slides have their own aspect ratio handling
 *   - unsupported — placeholder card, no content to center
 */
const CENTERED_KINDS = new Set(["markdown", "code", "text", "pdf", "image"]);

export function FilePreviewDialog({
  source,
  open,
  onOpenChange,
  actions,
}: FilePreviewDialogProps) {
  const [maximized, setMaximized] = useState(false);

  /**
   * Reset to default size when the dialog closes — otherwise a previously
   * maximized preview re-opens maximized and the toggle icon shows the
   * collapse glyph instead of expand. Mirrors the library-preview
   * dialog's behaviour.
   */
  useEffect(() => {
    if (!open) setMaximized(false);
  }, [open]);

  /**
   * Whether the source's content should be horizontally centered in a
   * max-width frame when the dialog is maximized. Resolved here so the
   * renderer registry stays oblivious to layout decisions — different
   * source kinds opt in via {@link CENTERED_KINDS}.
   */
  const shouldCenter = source ? CENTERED_KINDS.has(source.kind ?? "") : false;

  /**
   * The `open` prop alone drives Dialog visibility — callers that pass
   * `source: null` (e.g. a lazily-built source that isn't ready yet)
   * should also pass `open: false`. Letting the Dialog keep `source` in
   * memory through the close transition avoids the source-unmounts-
   * before-animation-completes glitch.
   */
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/**
       * `showCloseButton={false}` — the default shadcn close button is
       * absolutely-positioned and smaller than our action buttons, which
       * looks misaligned next to Download / Open-external. We render our
       * own X inside the toolbar so all four buttons share styling.
       *
       * Maximized state animates via `transition-all duration-300` on the
       * size classes — Radix's open/close animations run on transform +
       * opacity, so transitioning width/height in parallel doesn't fight
       * with the entrance transition.
       */}
      {/**
       * Maximized state goes truly edge-to-edge:
       *   - `max-w-[100vw]` + `w-screen` together override shadcn's
       *     default `max-w-[calc(100%-2rem)]`, which would otherwise
       *     reserve a 2rem outer margin.
       *   - Height is `--app-vh` (the VISIBLE viewport height published by
       *     `useVisualViewport`), falling back to `100dvh`. `h-screen`
       *     (=100vh) is the LARGE viewport on mobile — it ignores the
       *     browser's URL bar, so the dialog was taller than the screen.
       *   - The base shadcn content is CENTER-anchored (`top-50% left-50%`
       *     + `-translate-1/2`); combined with an over-tall height that
       *     pushed BOTH the header and footer off the top/bottom of a phone
       *     screen (but looked fine in a chromeless Playwright window). We
       *     re-anchor to the top-left and cancel the translate so it fills
       *     exactly the visible viewport.
       *   - `rounded-none` / `border-0` remove the frame at the edges.
       */}
      <DialogContent
        showCloseButton={false}
        className={cn(
          "p-0 gap-0 flex flex-col overflow-hidden transition-all duration-300",
          maximized
            ? "top-0 left-0 translate-x-0 translate-y-0 w-screen max-w-[100vw] sm:max-w-[100vw] h-[var(--app-vh,100dvh)] max-h-[var(--app-vh,100dvh)] rounded-none border-0"
            : "sm:max-w-6xl max-w-[90vw] w-[90vw] h-[85dvh] max-h-[85dvh] rounded-xl",
        )}
      >
        {source && (
          <>
            {/* Header — when maximized the dialog fills the visible viewport
                edge-to-edge (viewport-fit=cover), so pad the top/sides by the
                safe-area insets to clear the notch / status bar. Works for both
                Safari (URL bar at bottom) and Chrome (URL bar at top): the
                dialog is anchored to the top of the web content, which always
                sits below whatever browser chrome is above it. */}
            <div
              className="flex items-center gap-4 px-6 py-4 border-b bg-muted shrink-0"
              style={
                maximized
                  ? {
                      paddingTop: "calc(1rem + env(safe-area-inset-top))",
                      paddingLeft: "max(1.5rem, env(safe-area-inset-left))",
                      paddingRight: "max(1.5rem, env(safe-area-inset-right))",
                    }
                  : undefined
              }
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <FileText className="size-5 text-muted-foreground shrink-0" />
                <DialogTitle className="text-sm font-medium truncate">
                  {source.filename}
                </DialogTitle>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <TooltipProvider>
                  {actions}
                  <OpenExternalAction source={source} />
                  <DownloadAction source={source} />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setMaximized((v) => !v)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
                        aria-label={maximized ? "Restore size" : "Maximize"}
                      >
                        {maximized ? (
                          <Minimize2 className="size-4" />
                        ) : (
                          <Maximize2 className="size-4" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{maximized ? "Restore size" : "Maximize"}</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
                        aria-label="Close preview"
                      >
                        <X className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Close</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/**
             * Body — when maximized, document-like renderers (markdown,
             * code, text, pdf, image) sit inside a 1280px max-width
             * frame and are centered horizontally. The surrounding area
             * gets a muted backdrop so the centered content reads as a
             * "page" floating on a workspace, matching the reference.
             *
             * Edge-to-edge renderers (spreadsheet, html, presentation)
             * skip the centering wrapper and fill the dialog.
             */}
            <div
              className={cn(
                "flex-1 overflow-hidden",
                maximized && shouldCenter && "bg-muted/30",
              )}
            >
              <div
                className={cn(
                  "h-full",
                  maximized && shouldCenter && "max-w-[1280px] mx-auto",
                )}
              >
                <FilePreviewBody source={source} />
              </div>
            </div>

            {/* Footer — clear the home indicator / bottom safe area when the
                maximized dialog runs edge-to-edge. */}
            <div
              className="flex items-center justify-between px-4 py-3 border-t bg-muted shrink-0"
              style={
                maximized
                  ? {
                      paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
                      paddingLeft: "max(1rem, env(safe-area-inset-left))",
                      paddingRight: "max(1rem, env(safe-area-inset-right))",
                    }
                  : undefined
              }
            >
              <div className="text-xs text-muted-foreground">
                {(source.kind ?? source.contentType ?? "file").toUpperCase()}
                {source.size != null && ` • ${formatBytes(source.size)}`}
              </div>
              <div className="text-xs text-muted-foreground font-mono max-w-[50%] truncate">
                {source.filename}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
